import { type AriaAttributes, type FocusEventHandler, type ReactNode, type Ref } from "react";
import { TextField as MuiTextField, type AutocompleteRenderInputParams } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import { SelectValueAdornment } from "ui/select/components/select-value-adornment";

import type { OptionType, SelectSize } from "ui/select/select.types";

type SelectInputProps = {
  params: AutocompleteRenderInputParams;
  name: string;
  placeholder: string;
  hasError: boolean;
  isSearchable: boolean;
  /** The call site's `aria-*`. The field's own label and description are layered over them. */
  ariaProps?: AriaAttributes;
  ariaLabel: string;
  ariaDescribedBy?: string;
  autoFocus?: boolean;
  onBlur?: FocusEventHandler;
  inputRef: Ref<HTMLInputElement>;
  size: SelectSize;
  selectedOption: OptionType | null;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  selectedValueAction?: ReactNode;
};

/** A read-only input still shows a caret in some browsers and an I-beam everywhere; neither fits a picker. */
const NOT_SEARCHABLE_SX: SxProps<Theme> = {
  "& .MuiAutocomplete-input": { caretColor: "transparent", cursor: "pointer", userSelect: "none" },
};

/**
 * The text field `Autocomplete` renders. MUI's own adornments — the clear and popup buttons — stay in
 * place, with `startIcon` and the selected option's icon before the value and its alert glyph, `endIcon`
 * and value action after it. `size` goes through to MUI, which is what selects the `MuiOutlinedInput`
 * geometry those icons take.
 */
export const SelectInput = ({
  params,
  name,
  placeholder,
  hasError,
  isSearchable,
  ariaProps,
  ariaLabel,
  ariaDescribedBy,
  autoFocus,
  onBlur,
  inputRef,
  size,
  selectedOption,
  startIcon,
  endIcon,
  selectedValueAction,
}: SelectInputProps) => (
  <MuiTextField
    {...params}
    name={name}
    placeholder={placeholder}
    error={hasError}
    size={size}
    autoFocus={autoFocus}
    onBlur={onBlur}
    inputRef={inputRef}
    sx={isSearchable ? undefined : NOT_SEARCHABLE_SX}
    slotProps={{
      ...params.slotProps,
      input: {
        ...params.slotProps.input,
        startAdornment: (
          <>
            <SelectValueAdornment option={selectedOption} slot="start" startIcon={startIcon} />
            {params.slotProps.input.startAdornment}
          </>
        ),
        endAdornment: (
          <>
            <SelectValueAdornment option={selectedOption} slot="end" endIcon={endIcon} />
            {selectedValueAction}
            {params.slotProps.input.endAdornment}
          </>
        ),
      },
      htmlInput: {
        ...params.slotProps.htmlInput,
        ...ariaProps,
        "aria-label": ariaLabel,
        "aria-describedby": ariaDescribedBy,
        "data-bwignore": "true",
        readOnly: !isSearchable,
      },
    }}
  />
);
